import {
	type CommentaryLanguages,
	type CommentaryModules,
	CommentaryTemplateControllerApiFactory,
	type CommentaryTemplateDto,
	type CommentaryTemplateDtoToneOfVoiceEnum,
	type SaveCommentaryTemplateDto,
} from "@eu.mdotm.api/sphere";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { CollapsibleBase, Controller, ErrorBoundary, Icon, ProgressBar, Text } from "@mdotm/shadcn/legacy/components";
import {
	adaptAnimatedNodeProvider,
	spawn,
	useRefProxyWithState,
	useTick,
	useUniqueDOMId,
	useUnmountAbortSignal,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, nullary, unpromisify } from "@mdotm/shadcn/legacy/utils";
import {
	type DropdownDropdownItem,
	DropdownMenu,
	type DropdownTriggerProps,
	Select,
	TextArea,
	Button,
} from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import {
	$createParagraphNode,
	$createTextNode,
	$getRoot,
	$getSelection,
	type LexicalEditor,
	ParagraphNode,
	type SerializedEditorState,
	type SerializedElementNode,
	type SerializedLexicalNode,
} from "lexical";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { useApiGen } from "$root/api/hooks";
import Texture from "$root/assets/images/texture.png";
import RoundedButton from "$root/components/buttons/RoundedButton";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { Sidebar } from "$root/components/common/Sidebar";
import { IconWallBase } from "$root/components/IconWall";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { MarkdownRenderer, type MarkdownRendererProps } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus } from "$root/event-bus";
import type { Pill } from "./lexical/pills/common";
import { $createPillNode, PillNode } from "./lexical/pills/PillNode";
import PillsPlugin from "./lexical/pills/PillPlugin";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useMappedQuery, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { customObjectEntriesFn, customObjectValuesFn, qualifier } from "$root/utils";
import { portfolioRepository } from "../portfolio/repository";
import CommentaryLoading from "./box/CommentaryReady";
import { type AllowedModules, type StableConfiguration, stableDefaultConfiguration } from "./common";
import { storyfolioRepository } from "./repository";
import {
	InstumentInsightsModule,
	MarketOulookModule,
	PerformanceAnalysisModule,
	PortfolioCompositionModule,
	PortfolioDynamicModule,
	PortfolioMetricsModule,
} from "./storyfolio-components";

function LanguageTrigger({ state: _state, ...props }: DropdownTriggerProps) {
	return (
		<RoundedButton data-qualifier={qualifier.storyfolio.details.selectLanguage} {...props}>
			<div className="flex gap-2">
				<Icon icon="Language" size={18} />
				Languages
			</div>
		</RoundedButton>
	);
}

function ToneOfVoiceTrigger({ state: _state, ...props }: DropdownTriggerProps) {
	return (
		<RoundedButton data-qualifier={qualifier.storyfolio.details.selectTone} {...props}>
			<div className="flex gap-2">
				<Icon icon="Tone-of-voice" size={18} />
				Tone of voice
			</div>
		</RoundedButton>
	);
}

function AddModulesTrigger({ state: _state, ...props }: DropdownTriggerProps) {
	return (
		<RoundedButton className="ml-auto mr-0" data-qualifier={qualifier.storyfolio.details.selectModule} {...props}>
			<div className="flex gap-2">
				<Icon icon="blocks" size={18} />
				Add modules
			</div>
		</RoundedButton>
	);
}

/** @internal — exported for tests */
export function lexicalEditorStateToMarkdown(JSONEditorState: SerializedEditorState<SerializedLexicalNode>) {
	const lexialStateToJSON = JSONEditorState.root.children as SerializedElementNode[] | undefined;

	if (lexialStateToJSON) {
		const lexicalNodesToMarkdown = lexialStateToJSON.reduce((description, lexicalNode, i) => {
			const mainDescription = lexicalNode.children.reduce((subDescription, subLexicalNode) => {
				if (subLexicalNode.type === "text") {
					const textNode = subLexicalNode as SerializedLexicalNode & {
						text: string;
					};

					if (textNode.text === "\n") {
						return `${subDescription}\n`;
					}

					return subDescription + textNode.text;
				}

				if (subLexicalNode.type === "linebreak") {
					return `${subDescription}\n`;
				}

				if (subLexicalNode.type === "pill") {
					const pillNode = subLexicalNode as SerializedLexicalNode & {
						text: string;
					} & {
						pill: Pill<CommentaryModules>;
					};
					return `${subDescription}[[${pillNode.pill.value}]]`;
				}

				return subDescription;
			}, "");

			if (i > 0) {
				description += "\n";
			}

			description += mainDescription;

			return description;
		}, "");

		return lexicalNodesToMarkdown;
	}

	return "";
}

const customModules: Record<CommentaryModules, { label: string; value: CommentaryModules }> = {
	INSTRUMENT_INSIGHTS: {
		label: "#Instrument Insights",
		value: "INSTRUMENT_INSIGHTS",
	},
	MARKET_OUTLOOK: {
		label: "#Market View",
		value: "MARKET_OUTLOOK",
	},
	PORTFOLIO_COMPOSITION: {
		label: "#Portfolio Composition",
		value: "PORTFOLIO_COMPOSITION",
	},
	PORTFOLIO_DYNAMICS: {
		label: "#Portfolio Dynamics",
		value: "PORTFOLIO_DYNAMICS",
	},
	PORTFOLIO_METRICS: {
		label: "#Portfolio Metrics",
		value: "PORTFOLIO_METRICS",
	},
	PERFORMANCE_ANALYSIS: {
		label: "#Performance Analysis",
		value: "PERFORMANCE_ANALYSIS",
	},
};

const customModulesFieldsName = {
	INSTRUMENT_INSIGHTS: "instrumentInsightsBlock",
	MARKET_OUTLOOK: "marketOutlook",
	PORTFOLIO_COMPOSITION: "portfolioCompositionBlock",
	PORTFOLIO_DYNAMICS: "dynamicsBlock",
	PORTFOLIO_METRICS: "metricsBlock",
	PERFORMANCE_ANALYSIS: "performanceAnalysisBlock",
} satisfies Record<CommentaryModules, AllowedModules>;

const availablePills = customObjectValuesFn(customModules);

function StoryfolioInner(props: {
	template: SaveCommentaryTemplateDto & { uuid?: string };
	onSubmit: (template: SaveCommentaryTemplateDto) => MaybePromise<void>;
}): React.JSX.Element {
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();
	const lexicalEditorHandlerRef = useRef<LexicalEditor | null>(null);
	const [isEditorDirty, setIsEditorDirty] = useState(false);
	const [editor] = useRefProxyWithState(null, lexicalEditorHandlerRef);
	const { push } = useTypedNavigation();
	const [selectedModule, setSelectedModule] = useState("");
	const tick = useTick();
	const crumbs = useCrumbs();

	const { setValue, watch, getValues, handleSubmit, formState } = useForm({
		defaultValues: props.template,
	});

	const observedTemplate = watch();

	useEffect(() => {
		const unregister = lexicalEditorHandlerRef.current?.registerUpdateListener(({ editorState }) => {
			editorState.read(() => {
				const selection = $getSelection();
				if (selection?.dirty) {
					setIsEditorDirty(true);
				}
			});
		});

		return () => unregister?.();
	}, [editor]);

	const unmountAbortSignal = useUnmountAbortSignal();

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.storyfolio("Commentary builder", props.template.uuid),
					...crumbs.commentaryName({
						uuid: props.template.uuid,
						name: props.template.name,
					}),
				]}
				titleAction={
					<div className="flex justify-end items-center gap-2">
						<Button
							onClick={() => push("Storyfolio/Studio", {})}
							size="small"
							palette="tertiary"
							data-qualifier={qualifier.storyfolio.details.cancel}
						>
							{t("BUTTON.CANCEL")}
						</Button>
						<Button
							palette="primary"
							size="small"
							onClickAsync={() =>
								handleSubmit(async (values) => {
									setIsEditorDirty(false);
									await props.onSubmit({
										...values,
										structure: lexicalEditorHandlerRef.current?.getEditorState().toJSON()
											? lexicalEditorStateToMarkdown(lexicalEditorHandlerRef.current.getEditorState().toJSON())
											: values.structure,
									});
								})()
							}
							data-qualifier={qualifier.storyfolio.details.save}
						>
							{observedTemplate.default ? t("BUTTON.SAVE_AS") : t("BUTTON.SAVE")}
						</Button>
					</div>
				}
			/>
			<div className="grow min-h-0 flex flex-col rounded-md bg-white">
				<div className="flex space-x-2 h-full">
					<div className={`overflow-y-auto flex-1 flex flex-col border-r border-r-[var(--palette-N100)] h-full`}>
						<StyleBlock
							onChangeContext={(ctx) => setValue("context", ctx)}
							onChangeToneOfVoice={(tone) => {
								const currentTone = getValues("toneOfVoice");
								if (currentTone === tone) {
									return setValue("toneOfVoice", undefined);
								}

								setValue("toneOfVoice", tone);
							}}
							onChangeLanguage={(language) => setValue("language", language)}
							template={observedTemplate}
						/>
						<ContenStructureBlock
							template={observedTemplate}
							onModuleClick={({ value }) => {
								unpromisify(async () => {
									setSelectedModule(value);
									const newSearchParams = new URLSearchParams(location.search);
									newSearchParams.set("module", value);
									history.replace({ search: newSearchParams.toString() });
									await tick();
									spawnStoryfolioModuleSettings({
										signal: unmountAbortSignal,
										type: customModulesFieldsName[value],
										module: stableDefaultConfiguration[customModulesFieldsName[value]](
											getValues(customModulesFieldsName[value]) as any,
										) as any,
										onChange: (module: any) => setValue(customModulesFieldsName[value], module),
										onCancel: (reject) => {
											history.replace({ search: undefined });
											if (history.location.search === "" || history.location.search === undefined) {
												reject?.();
											}
										},
									})
										.promise.catch(noop)
										.finally(() => setSelectedModule(""));
								})();
							}}
							onAddModule={(module, payload) => setValue(module, payload as any)}
							onRemoveModule={(module, payload) => setValue(module, payload as any)}
							lexicalRef={lexicalEditorHandlerRef}
						/>
					</div>

					<div className="flex-1">
						<StoryfolioPreviewBlock template={observedTemplate} lexicalRef={lexicalEditorHandlerRef} />
					</div>
				</div>
			</div>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={formState.isDirty || isEditorDirty}
				showCancelIcon={false}
				pathToNotBlock={[
					history.location.pathname,
					selectedModule ? history.location.pathname + "?module=" + selectedModule : history.location.pathname,
				]}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}

function StyleBlock(props: {
	onChangeToneOfVoice(tone: CommentaryTemplateDtoToneOfVoiceEnum): void;
	onChangeLanguage(languange: CommentaryLanguages): void;
	onChangeContext(context: string): void;
	template: SaveCommentaryTemplateDto;
}) {
	const { template } = props;
	const [expandContext, setExpandContext] = useState(false);
	const { t } = useTranslation();

	return (
		<div className={`p-4 border-b border-b-[var(--palette-N100)]`}>
			<div className="flex items-center mb-2">
				<Text type="Body/XL/Bold" className="mr-2">
					Style
				</Text>
				<Button
					unstyled
					onClick={() => setExpandContext((expand) => !expand)}
					className="flex space-x-2 items-center"
					data-qualifier={qualifier.storyfolio.details.expandContext}
				>
					Edit text context <Icon icon={expandContext ? "Up" : "Down"} color={themeCSSVars.palette_N200} />
				</Button>

				<div className="flex space-x-2 ml-auto mr-0">
					<ReactQueryWrapper
						{...storyfolioRepository.languages.getQueryConfig()}
						loadingFallback={
							<RoundedButton
								className="cursor-progress animate-pulse"
								disabled
								data-qualifier={qualifier.storyfolio.details.selectLanguage}
							>
								<div className="flex gap-2">
									<Icon icon="Language" size={18} />
									Languages
								</div>
							</RoundedButton>
						}
					>
						{(languages) => (
							<DropdownMenu
								Trigger={LanguageTrigger}
								actions={languages.map(
									(language): DropdownDropdownItem => ({
										onClick: () => {
											props.onChangeLanguage(language as CommentaryLanguages);
										},
										icon: {
											name: "Outline",
											color: template.language === language ? undefined : "transparent",
										},
										"data-qualifier": qualifier.storyfolio.details.languageOption(language),
										label: t(`LANGUAGES.${language as CommentaryLanguages}`),
									}),
								)}
							/>
						)}
					</ReactQueryWrapper>

					<ReactQueryWrapper
						{...storyfolioRepository.tonesOfVoice.getQueryConfig()}
						loadingFallback={
							<RoundedButton
								className="cursor-progress animate-pulse"
								disabled
								data-qualifier={qualifier.storyfolio.details.selectTone}
							>
								<div className="flex gap-2">
									<Icon icon="Tone-of-voice" size={18} />
									Tone of voice
								</div>
							</RoundedButton>
						}
					>
						{(toneOfVoice) => (
							<DropdownMenu
								Trigger={ToneOfVoiceTrigger}
								actions={toneOfVoice.map(
									(tone): DropdownDropdownItem => ({
										onClick: () => {
											props.onChangeToneOfVoice(tone as CommentaryTemplateDtoToneOfVoiceEnum);
										},
										icon: {
											name: "Outline",
											color: template.toneOfVoice === tone ? undefined : "transparent",
										},
										"data-qualifier": qualifier.storyfolio.details.toneOption(tone),
										label: t(`TONE_OF_VOICE.${tone as CommentaryTemplateDtoToneOfVoiceEnum}`),
									}),
								)}
							/>
						)}
					</ReactQueryWrapper>
				</div>
			</div>
			<CollapsibleBase expand={expandContext}>
				<Controller value={template.context ?? ""} onChange={props.onChangeContext}>
					{({ onChange, onCommit, value }) => (
						<TextArea
							rows={4}
							className={{
								"[&>textarea]:resize-none overflow-y-auto": true,
							}}
							value={value}
							onChangeText={onChange}
							onBlur={nullary(onCommit)}
							data-qualifier={qualifier.storyfolio.details.contextValue}
						/>
					)}
				</Controller>
			</CollapsibleBase>
		</div>
	);
}

const contentEditableClassName = [
	"relative w-auto h-full z-0 min-w-0 max-w-none flex-1 rounded-[4px] border-2 placeholder:text-[#80848B] font-title",
	"overflow-y-auto text-ellipsis",
	"border-[#DFE2E7] active:border-[#667085] focus:outline-hidden focus:border-[#667085] disabled:border-[#DFE2E7]",
	"bg-[#ffffff] disabled:border-[#DFE2E7] disabled:text-[#A0A7B6] disabled:bg-[#EFF0F3]",
	"data-[invalid=true]:border-[#E81E25] text-[14px] py-[5px] px-2",
].join(" ");

/**
 * Lexical reads this prop both as an element type and as a `useMemo` dependency for the decorator
 * portals, so it has to keep a stable identity across renders or every pill remounts.
 */
function RichTextErrorBoundary(errorProps: {
	children: React.JSX.Element;
	onError: (error: Error) => void;
}): React.JSX.Element {
	return <ErrorBoundary {...errorProps} errorChildren="Ops..." />;
}

function RichTextEditor(props: {
	template: SaveCommentaryTemplateDto;
	onModuleClick(pill: Pill<CommentaryModules>): void;
	innerRef: RefObject<LexicalEditor | null>;
	onAddModule<K extends AllowedModules>(moduleType: K, payload: StableConfiguration[K]): void;
	onRemoveModule<K extends AllowedModules>(moduleType: K, payload: StableConfiguration[K]): void;
}) {
	const id = useUniqueDOMId();
	const [editor, setEditor] = useRefProxyWithState(null, props.innerRef);

	const mutationListerDataRef = useUnsafeUpdatedRef({
		onRemoveModule: props.onRemoveModule,
		onAddModule: props.onAddModule,
	});

	useEffect(() => {
		const unregister = editor?.registerMutationListener(PillNode, (mutatedNodes, { prevEditorState }) => {
			for (const [nodeKey, mutation] of mutatedNodes) {
				if (mutation === "created") {
					const node = editor.getEditorState()._nodeMap.get(nodeKey) as PillNode<CommentaryModules> | undefined;
					if (node !== undefined) {
						const pill = node.__pill;
						const key = customModulesFieldsName[pill.value];
						mutationListerDataRef.current.onAddModule(key, {
							...props.template[key],
							enabled: true,
						});
					}
				}

				if (mutation === "destroyed") {
					const node = prevEditorState._nodeMap.get(nodeKey);

					if (node) {
						const value = (node as PillNode<CommentaryModules>).__pill.value;
						const key = customModulesFieldsName[value];
						mutationListerDataRef.current.onRemoveModule(key, {
							...props.template[key],
							enabled: false,
						});
					}
				}
			}
		});

		const removeUpdateListener = editor?.registerNodeTransform(PillNode<CommentaryModules>, (node) => {
			if (node.__onClick === undefined) {
				const onClickFn = () => props.onModuleClick(node.__pill);
				node.setOnClick(onClickFn);
			}
		});

		return () => {
			unregister?.();
			removeUpdateListener?.();
		};
	}, [editor, mutationListerDataRef, props, props.template]);

	const selectablePills = useMemo(
		() =>
			availablePills.map(
				(x): Pill<CommentaryModules> => ({
					...x,
					disaled: props.template[customModulesFieldsName[x.value]]?.enabled,
				}),
			),
		[props.template],
	);

	return (
		<LexicalComposer
			initialConfig={{
				namespace: id,
				onError: console.error,
				nodes: [PillNode],
				editorState: (ctx) => {
					const root = $getRoot();
					if (root.getFirstChild() === null && props.template.structure) {
						const paragraph = $createParagraphNode();

						const pillValues = Object.values(customModules).map((x) => x.value);
						const customModuleLabelsByValue = Object.fromEntries(
							Object.values(customModules).map(({ label, value }) => [value, label]),
						);
						const availableNodesRegex = new RegExp(`\\[\\[(${pillValues.join("|")})\\]\\]`, "g");
						let captured: RegExpExecArray | null;
						let lastIndex = 0;
						while ((captured = availableNodesRegex.exec(props.template.structure)) !== null) {
							const pill = {
								label: customModuleLabelsByValue[captured[1]],
								value: captured[1] as CommentaryModules,
							} satisfies Pill<CommentaryModules>;

							paragraph.append($createTextNode(props.template.structure.slice(lastIndex, captured.index)));
							const pillNode = $createPillNode(pill, () => props.onModuleClick(pill));

							paragraph.append(pillNode);
							lastIndex = availableNodesRegex.lastIndex;
						}
						paragraph.append($createTextNode(props.template.structure.slice(lastIndex)));

						root.append(paragraph);
					}

					setEditor(ctx);
				},
			}}
		>
			<RichTextPlugin
				placeholder={null}
				ErrorBoundary={RichTextErrorBoundary}
				contentEditable={<ContentEditable className={contentEditableClassName} />}
				data-qualifier={qualifier.storyfolio.details.contentValue}
			/>
			{/* <OnChangePlugin onChange={onChange} /> */}
			<HistoryPlugin />
			<PillsPlugin
				pills={selectablePills}
				onClick={(pill) => props.onModuleClick({ ...pill })}
				onSelect={({ value }) =>
					props.onAddModule(
						customModulesFieldsName[value],
						props.template[customModulesFieldsName[value]]
							? {
									...props.template[customModulesFieldsName[value]],
									enabled: true,
								}
							: stableDefaultConfiguration[customModulesFieldsName[value]](undefined),
					)
				}
			/>
		</LexicalComposer>
	);
}

function ContenStructureBlock(props: {
	template: SaveCommentaryTemplateDto;
	onModuleClick(pill: Pill<CommentaryModules>): void;
	lexicalRef: RefObject<LexicalEditor | null>;
	onAddModule<K extends AllowedModules>(moduleType: K, payload: StableConfiguration[K]): void;
	onRemoveModule<K extends AllowedModules>(moduleType: K, payload: StableConfiguration[K]): void;
}) {
	const actions = useMemo<DropdownDropdownItem[]>(
		() =>
			customObjectEntriesFn(customModules).map(([, x]) => ({
				onClick: () => {
					const fieldName = customModulesFieldsName[x.value];
					props.onAddModule(
						fieldName,
						props.template[fieldName]
							? { ...props.template[fieldName], enabled: true }
							: stableDefaultConfiguration[fieldName](undefined),
					);
					props.lexicalRef.current?.update(() => {
						const module = $createPillNode(x, () => props.onModuleClick(x));
						const toAdd = [$createTextNode(" "), module, $createTextNode(" ")];

						const selection = $getSelection();
						if (selection) {
							const existingNode = selection.getNodes().at(-1);
							if (existingNode && existingNode instanceof PillNode) {
								toAdd.forEach((it) => existingNode.insertAfter(it));
							} else {
								selection.insertNodes(toAdd);
							}
						} else {
							const root = $getRoot();
							const lastChild = root.getLastChild();
							if (lastChild && lastChild instanceof ParagraphNode) {
								lastChild.append(...toAdd);
							} else {
								const paragraph = $createParagraphNode();
								paragraph.append(...toAdd);
								root.append(paragraph);
							}
						}
					});
				},
				disabled: props.template[customModulesFieldsName[x.value]]?.enabled,
				"data-qualifier": qualifier.storyfolio.details.moduleOption(x.value),
				label: `${x.label}`,
			})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.template, props.lexicalRef.current],
	);
	return (
		<div className="flex-1 flex flex-col">
			<div className="flex p-4">
				<Text type="Body/XL/Bold">Content structure</Text>

				<DropdownMenu Trigger={AddModulesTrigger} actions={actions} />
			</div>
			<div className="px-4 pb-4 grow">
				<RichTextEditor
					onModuleClick={props.onModuleClick}
					onAddModule={props.onAddModule}
					onRemoveModule={props.onRemoveModule}
					template={props.template}
					innerRef={props.lexicalRef}
				/>
			</div>
		</div>
	);
}

function spawnStoryfolioModuleSettings(
	params: { signal?: AbortSignal } & {
		[K in AllowedModules]: {
			type: K;
			module: StableConfiguration[K];
			onChange: (module: StableConfiguration[K]) => void;
		} & {
			onCancel?(reject?: (err?: unknown) => void): void;
		};
	}[AllowedModules],
) {
	const { onChange } = params;

	const map = {
		instrumentInsightsBlock: InstumentInsightsModule,
		marketOutlook: MarketOulookModule,
		portfolioCompositionBlock: PortfolioCompositionModule,
		dynamicsBlock: PortfolioDynamicModule,
		metricsBlock: PortfolioMetricsModule,
		performanceAnalysisBlock: PerformanceAnalysisModule,
	} satisfies Record<AllowedModules, unknown>;

	const Component = map[params.type as "dynamicsBlock"];

	function onCancel(reject?: (err?: unknown) => void) {
		params.onCancel?.(reject);
	}

	return spawn<void>(
		adaptAnimatedNodeProvider(({ onHidden, reject, resolve, show }) => (
			<Sidebar
				open={show}
				onClose={() => onCancel(reject)}
				width={440}
				mode="modal"
				onAnimationStateChange={(s) => s === "exited" && onHidden()}
			>
				<Component
					module={params.module as StableConfiguration["dynamicsBlock"]}
					onCancel={() => onCancel(reject)}
					onChange={(newInstrumentInsightsBlockModel) => {
						(onChange as (module: StableConfiguration["dynamicsBlock"]) => void)(newInstrumentInsightsBlockModel);
						resolve();
					}}
				/>
			</Sidebar>
		)),
		{ signal: params.signal },
	);
}

function StoryfolioPreviewBlock(props: {
	template: Omit<CommentaryTemplateDto, "uuid"> & { uuid?: string };
	lexicalRef: RefObject<LexicalEditor | null>;
}) {
	const [selectedInvestment, setSelectedInvestment] = useState<string | null>(null);
	const [preview, setPreview] = useState<string | undefined>(undefined);
	const { lexicalRef } = props;

	const commentaryTemplateApi = useApiGen(CommentaryTemplateControllerApiFactory);
	const commentaryPreview = useQueryNoRefetch(["queryCommentaryTemplatePreview"], {
		enabled: selectedInvestment !== null && props.template.structure !== "" && props.template.structure !== undefined,
		queryFn: () => {
			let structure: string = "";
			const JSONEditorState = lexicalRef.current?.getEditorState().toJSON();
			if (JSONEditorState) {
				structure = lexicalEditorStateToMarkdown(JSONEditorState);
			}
			return axiosExtract(
				commentaryTemplateApi.preview(selectedInvestment ?? "", {
					...props.template,
					structure: structure ?? props.template.structure,
				}),
			);
		},
	});
	useQueryReactor(commentaryPreview, {
		onSuccess: (content) => setPreview(content),
		onError: () => setPreview(undefined),
	});

	const selectablePortfoliosQuery = portfolioRepository.editorAvailablePortfolios.useQuery(null, "INVESTMENT");
	const querySelectablePortfolioTemplate = useMappedQuery(
		(selectablePortfolios) => {
			return selectablePortfolios?.flatMap((x) =>
				x.name && x.uuid && x.status !== "DRAFT"
					? [
							{
								label: x.name,
								value: x.uuid,
							},
						]
					: [],
			);
		},
		[],
		selectablePortfoliosQuery,
	);
	useQueryReactor(querySelectablePortfolioTemplate, {
		onSuccess: (opt) => setSelectedInvestment(opt?.[0]?.value ?? null),
	});

	useEventBus("investment-update", () => {
		querySelectablePortfolioTemplate.refetch({ throwOnError: false }).catch(noop);
	});

	return (
		<div
			className="flex flex-col overflow-hidden h-full pb-3 bg-repeat"
			style={{
				backgroundImage: `url(${Texture})`,
			}}
		>
			<div className="flex p-4 mb-4 items-center space-x-2">
				<Text type="Body/XL/Bold" as="span">
					Preview on
				</Text>
				<ReactQueryWrapperBase
					query={querySelectablePortfolioTemplate}
					loadingFallback={
						<Select
							options={[]}
							value=""
							size="small"
							searchPlaceholder="searching for portfolios.."
							disabled
							className="cursor-progress animate-pulse w-[250px]"
							data-qualifier={qualifier.storyfolio.details.selectPortfolio}
						/>
					}
				>
					{(options) => (
						<Select
							options={options}
							value={selectedInvestment}
							onChange={setSelectedInvestment}
							className="w-[250px]"
							size="small"
							searchPlaceholder="select a portfolio.."
							data-qualifier={qualifier.storyfolio.details.selectPortfolio}
							enableSearch
						/>
					)}
				</ReactQueryWrapperBase>
				<Button
					palette="secondary"
					size="small"
					disabled={commentaryPreview.isFetching || querySelectablePortfolioTemplate.isFetching}
					onClickAsync={() => commentaryPreview.refetch()}
					data-qualifier={qualifier.storyfolio.details.generatePreview}
				>
					<Icon
						icon="sphere-ai"
						size={16}
						color={
							commentaryPreview.status === "loading" || querySelectablePortfolioTemplate.status === "loading"
								? themeCSSVars.Button_fg_secondary_disabled
								: themeCSSVars.palette_P400
						}
					/>
					&nbsp;Generate
				</Button>
			</div>
			<div className="px-2 pb-2 flex flex-col  flex-1 overflow-y-auto">
				<div className="flex flex-col flex-1">
					{commentaryPreview.isFetching || selectedInvestment === null ? (
						<IconWallBase>
							<ProgressBar
								value="indeterminate"
								accentColor={themeCSSVars.palette_A300}
								backgroundColor={themeCSSVars.palette_N100}
								className="absolute top-0 inset-x-0"
								barHeight={8}
							/>
							<div className="flex flex-col">
								<h4 className="text-center">Calculating...</h4>
							</div>
						</IconWallBase>
					) : preview ? (
						<div
							className="p-8 grow bg-white shadow-xl"
							data-qualifier={qualifier.storyfolio.details.generatedPreviewSuccessfully}
						>
							<MarkdownRenderer componentOverrides={markdownOverrides}>{preview}</MarkdownRenderer>
						</div>
					) : (
						<div className="flex flex-col flex-1">
							<IconWallBase>
								{/** addign missing illustration */}

								<div className="text-center">
									<div className="mx-auto w-fit">
										<CommentaryLoading />
									</div>

									<Text as="p" type="Body/L/Bold">
										Preview in not available yet
									</Text>
									<Text as="p" type="Body/M/Book">
										Complete structure and press update preview
									</Text>
								</div>
							</IconWallBase>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

const markdownOverrides: MarkdownRendererProps["componentOverrides"] = {
	table: ({ node: _node, ...props }) => <table className="w-full border-collapse" {...props} />,
	thead: ({ node: _node, ...props }) => <thead {...props} />,
	tr: ({ node: _node, ...props }) => (
		<tr className={`even:bg-[#F7F8F9] border-b border-b-[color:var(--palette-N100)]`} {...props} />
	),
	td: ({ node: _node, ...props }) => <td className="text-left p-2 text-[10px]! first:w-2/4" {...props} />,
	th: ({ node: _node, ...props }) => (
		<th className="text-left px-2 py-1 font-bold! text-[10px]! uppercase! text-[#667085]" {...props} />
	),
	tbody: ({ node: _node, ...props }) => <tbody {...props} />,
};

export default StoryfolioInner;
